import { AfterViewInit, Component, Injectable, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { HotWireComponent } from './hot-wire/hot-wire.component';
import { cast, IQuestionnaireGroup, Messgeraete } from '@interfaces/interfaces';
import { BlockQuestionItem, RangeQuestion, SpecialQuestionItem, SpecialQuestionType } from '@interfaces/questions';
import { WorkflowItemKeyDirective } from '@workflow/directives/workflow-item-key/workflow-item-key.directive';
import { Subscription } from 'rxjs';
import { WizardPage } from '@app/workflow/components/navigator/wizard-view/interfaces';
import { AbstractSpecialQuestionComponent, ISpecialQuestion, ISpecialQuestionComponent } from '../special-question.service';
import { isNil } from 'lodash';
import { VisuomotorischeKoordinationEpilogComponent } from './visuomotorische-koordination-epilog.component';

@Injectable()
export class VisuomotorischeKoordination implements ISpecialQuestion {
	public readonly component = VisuomotorischeKoordinationComponent;
	public readonly specialQuestionType: SpecialQuestionType = 'VisuomotorischeKoordination';
	public readonly requiredDevices = [Messgeraete.TouchScreen, Messgeraete.SoundDevice];

	title = 'Visuomotorische Koordination';
	missingValueKeyGroup = 'fehlwert';

	public readonly defaultGroupConfiguration: IQuestionnaireGroup = {
		title: this.title,
		preface: ['<div class="markdown-html">Zum Test der visuomotorischen Koordinationsfähigkeit wird Ihnen auf der nächsten Seite eine ' +
		'blaue Schlangenlinie gezeigt. Ihre Aufgabe ist es, diese Linie so schnell und genau wie möglich mit dem Stift nachzufahren. ' +
		'Hierbei wird es als Fehler gewertet, wenn Sie von der Linie abkommen oder Sie den Stift absetzen. ' +
		'Bitte vermeiden Sie es, die Hand aufzulegen. ' +
		'Sobald Sie mit dem Stift den grün markierten Bereich verlassen, wird die Zeit gestartet, ' +
		'sobald Sie die orangene Fläche am Ende der Linie erreichen, wird die Zeit automatisch gestoppt.<br>'+
		`<div class="text-center mt-5"><img src="assets/images/tests/VisuomotorischeKoordination.png" alt="Visuomotorische Koordination Beispiel" width="600px"/></div></div>`],
		items: [
			cast<BlockQuestionItem>({
				title: this.title,
				type: 'BlockQuestion',
				items: [
					{
						key: 'zeit',
						label: 'Zeit',
						question: cast<RangeQuestion>({
							type: 'range',
							unit: 'sek',
							rangeLow: 0,
							rangeHigh: 200
						})
					},
					{
						key: 'fehler',
						label: 'Fehler',
						question: cast<RangeQuestion>({
							type: 'range',
							rangeLow: 0,
							rangeHigh: 100
						})
					}
				],
				isProcedure: true,
			})
		]
	};

	public isCompleted(currentValue: {zeit?: number, fehler?: number}): boolean {
		return !isNil(currentValue?.zeit) && !isNil(currentValue?.fehler);
	}

	buildWizardPages(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem): WizardPage[] {
		specialQuestion.description = 'Während des Tests darf nur der Stift den Bildschirm berühren! Bitte vermeiden Sie es, die Hand aufzulegen.';
		return [{
			...group,
			question: specialQuestion,
			subpage: 1,
			obligatoryUnskippableKeys: [specialQuestion.key],
			obligatoryAllKindOfKeys: [specialQuestion.key],
		}];
	}

	buildEpilogPages(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem): WizardPage[] {
		return [{
			...group,
			question: {
				type: 'epilog',
				title: group.title,
				centered: true,
			},
			obligatoryUnskippableKeys: [],
			obligatoryAllKindOfKeys: [],
			disableNext: false,
			isProcedure: true,
			alignBottom: true,
			epilogComponent: VisuomotorischeKoordinationEpilogComponent,
			key: specialQuestion.key,
		}];
	}

}

@Component({
	selector: 'workflow-visuomotorische-koordination',
	templateUrl: './visuomotorische-koordination.component.html',
	styleUrls: ['./visuomotorische-koordination.component.scss',
		'../../../../../../workflow/components/navigator/wizard-view/wizard-view.component.scss']
})
export class VisuomotorischeKoordinationComponent extends AbstractSpecialQuestionComponent implements OnInit, OnDestroy, AfterViewInit, ISpecialQuestionComponent {

	@ViewChild(HotWireComponent)
	private hotWire: HotWireComponent;

	private subscription = new Subscription();

	constructor(private workflowItemKey: WorkflowItemKeyDirective, router: Router) {
		super(router);
	}

	ngOnInit(): void {
		this.subscription.add(this.workflowItemKey.propertyFull$.subscribe(() => {
			if (this.finishedAndLocked) {
				this.goToEpilog();
			}
		}));

		this.valid.emit(false);
	}

	ngAfterViewInit(): void {
		this.subscription.add(this.hotWire.results.subscribe(results => {
			this.workflowItemKey.setProperty(results);
			this.valid.emit(true);
		}));
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}
